import { useCallback } from 'react';
import { DragSourceMonitor } from 'react-dnd';
import { useEditorStore, ILayoutComponent } from '@diana/core';
import {
  IDndLayoutComponentDragObject as IDragObject,
  IDndLayoutComponentDropResult as IDropResult,
} from '../../../../../providers/DndLayoutComponents2';
import { isEqual } from 'lodash';

interface IDndComponentDragEndEventData {
  dragObject: IDragObject;
  monitor: DragSourceMonitor<IDragObject, IDropResult>;
}

export const useDndComponentDragEndEvent = () => {
  const getLayoutById = useEditorStore((s) => s.getLayoutById);
  const updateLayoutById = useEditorStore((s) => s.updateLayoutById);

  const onDndComponentDragEnd = useCallback(
    (args: IDndComponentDragEndEventData) => {
      const { dragObject, monitor } = args;

      const dropResult = monitor.getDropResult();

      if (!dropResult) {
        const layout = dragObject.location ? getLayoutById(dragObject.location.layoutId) : null;
        const originLayout = dragObject.origin ? getLayoutById(dragObject.origin.layoutId) : null;

        const origin = dragObject.origin;
        const location = dragObject.location;

        const descendants = dragObject.descendants;
        const descendantsMap = descendants.reduce<Record<string, ILayoutComponent>>(
          (map, descendant) => ({ ...map, [descendant.id]: descendant }),
          {},
        );

        if (layout && location && !origin) {
          updateLayoutById(layout.id, (draft) => {
            draft.components.splice(location.index, 1);

            if (descendants.length) {
              draft.components = draft.components.filter((c) => !descendantsMap[c.id]);
            }

            return draft;
          });
        }

        if (layout && originLayout && location && origin) {
          // same layout
          if (layout.id === originLayout.id && !isEqual(location, origin)) {
            updateLayoutById(layout.id, (draft) => {
              const [component] = draft.components.splice(location.index, 1);

              const index = origin.index;
              const parentId = origin.parentId;
              draft.components.splice(index, 0, { ...component, parentId });

              if (descendants.length) {
                draft.components = draft.components.filter((c) => !descendantsMap[c.id]);
                location.index = draft.components.findIndex(
                  (c) => c.id === dragObject.component.id,
                );
                draft.components.splice(location.index + 1, 0, ...descendants);
              }

              return draft;
            });

            return location;
          }

          // different layout
          if (layout.id !== originLayout.id) {
            const component = layout.components[location.index];

            updateLayoutById(originLayout.id, (draft) => {
              const index = origin.index;
              const parentId = origin.parentId;
              draft.components.splice(index, 0, { ...component, parentId }, ...descendants);
              return draft;
            });

            updateLayoutById(layout.id, (draft) => {
              draft.components.splice(location.index, 1);

              if (descendants.length) {
                draft.components = draft.components.filter((c) => !descendantsMap[c.id]);
              }

              return draft;
            });
          }
        }
      }
    },
    [getLayoutById, updateLayoutById],
  );

  return { onDndComponentDragEnd };
};
